import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/seo/SEOHead';
import { Button } from '@/components/ui/button';
import GenomeRadar, { GenomeComparisonBars } from '@/components/genome/GenomeRadar';
import { Loader2, Trophy, ArrowRight, CheckCircle, XCircle, Minus, Dna } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EventComparePage() {
  const { eventSlug1, eventSlug2 } = useParams();
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchComparison();
  }, [eventSlug1, eventSlug2]);

  const fetchComparison = async () => {
    try {
      // First get event IDs from slugs
      const eventsResponse = await axios.get(`${BACKEND_URL}/api/public/events?limit=200`);
      const allEvents = [];
      
      for (const city of eventsResponse.data.cities || []) {
        for (const venue of city.venues || []) {
          for (const event of venue.events || []) {
            allEvents.push(event);
          }
        }
      }

      const event1 = allEvents.find(e => e.slug === eventSlug1);
      const event2 = allEvents.find(e => e.slug === eventSlug2);

      if (!event1 || !event2) {
        setError('One or both events not found');
        return;
      }

      const response = await axios.get(
        `${BACKEND_URL}/api/genome/compare/${event1.id}/${event2.id}`
      );

      setComparison(response.data.comparison);
    } catch (err) {
      setError('Failed to load comparison');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      </MainLayout>
    );
  }

  if (error || !comparison) {
    return (
      <MainLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-500 mb-4">{error || 'Comparison not available'}</p>
            <Link to="/events">
              <Button>Browse Events</Button>
            </Link>
          </div>
        </div>
      </MainLayout>
    );
  }

  const { event_1, event_2, differences, recommendations, seo_summary } = comparison;
  const winner = event_1.overall_score > event_2.overall_score ? event_1 : event_2;

  return (
    <MainLayout>
      <SEOHead
        title={`${event_1.name} vs ${event_2.name} - Event Comparison`}
        description={seo_summary?.meta_description}
      />

      {/* Hero */}
      <section className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Dna className="w-5 h-5 text-orange-400" />
            <span className="text-orange-400 font-medium">Event Genome Comparison</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-center mb-8">
            {event_1.name} <span className="text-gray-500">vs</span> {event_2.name}
          </h1>

          {/* Score cards */}
          <div className="grid md:grid-cols-3 gap-6 items-center">
            <div className={`bg-white/10 rounded-xl p-6 text-center ${winner.id === event_1.id ? 'ring-2 ring-orange-500' : ''}`}>
              <h2 className="font-semibold mb-2">{event_1.name}</h2>
              <div className="text-4xl font-bold text-orange-400">{event_1.overall_score}</div>
              <p className="text-gray-400 text-sm">Overall Score</p>
              {winner.id === event_1.id && (
                <span className="inline-flex items-center gap-1 mt-2 text-orange-400 text-sm">
                  <Trophy className="w-4 h-4" /> Winner
                </span>
              )}
            </div>

            <div className="text-center">
              <div className="text-6xl font-bold text-gray-600">VS</div>
            </div>

            <div className={`bg-white/10 rounded-xl p-6 text-center ${winner.id === event_2.id ? 'ring-2 ring-blue-500' : ''}`}>
              <h2 className="font-semibold mb-2">{event_2.name}</h2>
              <div className="text-4xl font-bold text-blue-400">{event_2.overall_score}</div>
              <p className="text-gray-400 text-sm">Overall Score</p>
              {winner.id === event_2.id && (
                <span className="inline-flex items-center gap-1 mt-2 text-blue-400 text-sm">
                  <Trophy className="w-4 h-4" /> Winner
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Comparison */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Detailed Comparison</h2>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Genome Radar Charts */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-4 text-center">{event_1.name}</h3>
              <div className="flex justify-center">
                <GenomeRadar genome={event_1.genome} size={280} />
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-4 text-center">{event_2.name}</h3>
              <div className="flex justify-center">
                <GenomeRadar genome={event_2.genome} size={280} />
              </div>
            </div>
          </div>

          {/* Bar comparison */}
          <div className="mt-12 bg-gray-50 rounded-xl p-8">
            <h3 className="font-semibold text-gray-900 mb-6 text-center">Head-to-Head</h3>
            <GenomeComparisonBars
              genome1={event_1.genome}
              genome2={event_2.genome}
              name1={event_1.name}
              name2={event_2.name}
            />
          </div>
        </div>
      </section>

      {/* Recommendations */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Which Should You Choose?</h2>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              { key: 'for_networking', label: 'Best for Networking', icon: '🤝' },
              { key: 'for_learning', label: 'Best for Learning', icon: '📚' },
              { key: 'for_deals', label: 'Best for Business Deals', icon: '💰' },
              { key: 'for_innovation', label: 'Best for Innovation', icon: '💡' },
              { key: 'best_value', label: 'Best Value for Money', icon: '⭐' },
              { key: 'overall_winner', label: 'Overall Winner', icon: '🏆' },
            ].map(item => {
              const winnerId = recommendations[item.key];
              const winnerEvent = winnerId === event_1.id ? event_1 : event_2;
              const isEvent1 = winnerId === event_1.id;

              return (
                <div
                  key={item.key}
                  className={`p-4 rounded-lg border-2 ${
                    isEvent1 ? 'border-orange-200 bg-orange-50' : 'border-blue-200 bg-blue-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">
                      {item.icon} {item.label}
                    </span>
                    <span className={`font-semibold ${isEvent1 ? 'text-orange-600' : 'text-blue-600'}`}>
                      {winnerEvent.name.length > 25 ? winnerEvent.name.substring(0, 25) + '...' : winnerEvent.name}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="mt-8 bg-white rounded-xl p-6 border">
            <h3 className="font-semibold text-gray-900 mb-3">Bottom Line</h3>
            <p className="text-gray-600">{seo_summary?.bottom_line}</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-orange-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">Ready to Attend?</h2>
          <p className="text-orange-100 mb-6">Get group accommodation and meeting rooms for your team.</p>
          <Link to="/events">
            <Button className="bg-white text-orange-600 hover:bg-gray-100">
              Browse Events <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </MainLayout>
  );
}